import React, { useMemo } from 'react';

// Components
import ComparisonItemSelectorUI from './ComparisonItemSelectorUI';

// Libraries
import { useSelector } from 'react-redux';

// Types
import ComparisonItem from '@/types/comparison-item';
import { ReduxState } from '@/redux/modules/types';

// Utils
import { formatLocalizedString } from '@/utils/i18n';

interface ComparisonItemSelectorProps {
  addLabel?: string;
  availableComparisonItems: ComparisonItem[];
  onRemoveItem: (index: number) => void;
  onSelectItem: (item: ComparisonItem, index: number) => void;
  selectedItemIndex: number;
}

const ComparisonItemSelector: React.FC<ComparisonItemSelectorProps> = ({
  addLabel = 'Comparison.AddToCompare',
  availableComparisonItems,
  onRemoveItem,
  onSelectItem,
  selectedItemIndex,
}) => {
  const selectedItems = useSelector((state: ReduxState) => state.comparison.selectedComparisonItems);

  const [firstItem, secondItem] = selectedItems || [];
  const selectedItem = selectedItems[selectedItemIndex];

  const formattedItemName = useMemo(() => (selectedItem ? formatLocalizedString(selectedItem.name) : ''), [selectedItem]);

  const hasNoItems = !availableComparisonItems.length;
  const isSecondSelector = selectedItemIndex === 1;
  const onlyOneAvailableAndIsSecond = firstItem && availableComparisonItems.length === 1 && isSecondSelector;
  const noFirstItemAndIsSecond = !firstItem && isSecondSelector;
  const isSelectDisabled = hasNoItems || onlyOneAvailableAndIsSecond || noFirstItemAndIsSecond;

  const availableOptions = useMemo(() => {
    const options =
      availableComparisonItems?.map(option => ({
        value: option._id,
        label: formatLocalizedString(option.name),
      })) || [];

    return options.filter(option => option.value !== (selectedItemIndex === 0 ? secondItem?._id : firstItem?._id) && option.value !== selectedItem?._id);
  }, [availableComparisonItems, selectedItemIndex, firstItem?._id, secondItem?._id, selectedItem?._id]);

  return (
    <ComparisonItemSelectorUI
      addLabel={addLabel}
      availableOptions={availableOptions}
      formattedItemName={formattedItemName}
      isSelectDisabled={isSelectDisabled}
      onRemoveItem={() => onRemoveItem(selectedItemIndex)}
      onSelectItem={(item: ComparisonItem) => onSelectItem(item, selectedItemIndex)}
      selectedItem={selectedItem}
      isOriginal={selectedItemIndex === 0}
      availableComparisonItems={availableComparisonItems}
    />
  );
};

export default ComparisonItemSelector;
